import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const ProfileDropdown = ({ displayName, onLogout, onOpenChange, shouldClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const handleToggle = useCallback(() => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (onOpenChange) {
      onOpenChange(newState);
    }
  }, [isOpen, onOpenChange]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    if (onOpenChange) {
      onOpenChange(false);
    }
  }, [onOpenChange]);

  useEffect(() => {
    if (shouldClose && isOpen) {
      handleClose();
    }
  }, [shouldClose, isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (!menuRef.current) {
        return;
      }
      if (!menuRef.current.contains(event.target)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isOpen, handleClose]);

  const handleTutorialClick = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const handleLogoutClick = useCallback(() => {
    handleClose();
    if (onLogout) {
      onLogout();
    }
  }, [handleClose, onLogout]);

  const handleSupportNavigate = useCallback((path) => {
    handleClose();
    navigate(path);
  }, [handleClose, navigate]);

  useEffect(() => {
    if (!isOpen) {
      setSupportOpen(false);
    }
  }, [isOpen]);

  return (
    <div className="relative hidden md:block">
      <button
        type="button"
        onClick={handleToggle}
        className="flex h-9 items-center gap-2 rounded-full border border-white/50 bg-white/90 px-4 text-sm font-semibold text-ponpon-navy shadow-sm transition hover:border-ponpon-coral/50 hover:text-ponpon-coral"
      >
        <span className="leading-none">{displayName}</span>
      </button>
      {isOpen ? (
        <div
          ref={menuRef}
          className="absolute right-0 top-[calc(100%+0.75rem)] z-20 w-56 rounded-lg border border-white/40 bg-white/95 backdrop-blur-xl shadow-lg"
        >
          <nav className="py-2" aria-label="Profile menu">
            {/* TODO: Uncomment when implementing profile features
            <button
              type="button"
              onClick={() => handleMenuClick('account')}
              className="w-full px-4 py-2.5 text-left text-sm font-medium text-ponpon-navy transition hover:bg-ponpon-coral/10 hover:text-ponpon-coral"
            >
              Account Settings
            </button>
            <button
              type="button"
              onClick={() => handleMenuClick('shipping')}
              className="w-full px-4 py-2.5 text-left text-sm font-medium text-ponpon-navy transition hover:bg-ponpon-coral/10 hover:text-ponpon-coral"
            >
              Shipping Addresses
            </button>
            <button
              type="button"
              onClick={() => handleMenuClick('activity')}
              className="w-full px-4 py-2.5 text-left text-sm font-medium text-ponpon-navy transition hover:bg-ponpon-coral/10 hover:text-ponpon-coral"
            >
              Activity
            </button>
            <button
              type="button"
              onClick={() => handleMenuClick('preferences')}
              className="w-full px-4 py-2.5 text-left text-sm font-medium text-ponpon-navy transition hover:bg-ponpon-coral/10 hover:text-ponpon-coral"
            >
              Preferences
            </button>
            <button
              type="button"
              onClick={() => handleMenuClick('about')}
              className="w-full px-4 py-2.5 text-left text-sm font-medium text-ponpon-navy transition hover:bg-ponpon-coral/10 hover:text-ponpon-coral"
            >
              About
            </button>
            <div className="my-1 border-t border-white/20"></div>
            */}
            <Link
              to="/tutorial"
              onClick={handleTutorialClick}
              className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-ponpon-navy transition hover:bg-ponpon-coral/10 hover:text-ponpon-coral"
            >
              <span className="text-base leading-none">📖</span>
              <span>How to Play</span>
            </Link>
            <div className="my-1 border-t border-white/20"></div>
            <div className="px-4 py-2">
              <button
                type="button"
                onClick={() => setSupportOpen((prev) => !prev)}
                className="flex w-full items-center justify-between text-left text-xs font-semibold uppercase tracking-wide text-ponpon-navy/60 transition hover:text-ponpon-coral"
                aria-expanded={supportOpen}
              >
                <span>Support</span>
                <span className="text-base leading-none">{supportOpen ? '−' : '+'}</span>
              </button>
              {supportOpen ? (
                <div className="mt-2 flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => handleSupportNavigate('/about')}
                    className="text-left text-sm font-medium text-ponpon-navy transition hover:bg-ponpon-coral/10 hover:text-ponpon-coral px-2 py-1.5 rounded"
                  >
                    About
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSupportNavigate('/faq')}
                    className="text-left text-sm font-medium text-ponpon-navy transition hover:bg-ponpon-coral/10 hover:text-ponpon-coral px-2 py-1.5 rounded"
                  >
                    FAQ
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSupportNavigate('/terms')}
                    className="text-left text-sm font-medium text-ponpon-navy transition hover:bg-ponpon-coral/10 hover:text-ponpon-coral px-2 py-1.5 rounded"
                  >
                    Terms of Service
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSupportNavigate('/privacy')}
                    className="text-left text-sm font-medium text-ponpon-navy transition hover:bg-ponpon-coral/10 hover:text-ponpon-coral px-2 py-1.5 rounded"
                  >
                    Privacy Policy
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSupportNavigate('/return-refund')}
                    className="text-left text-sm font-medium text-ponpon-navy transition hover:bg-ponpon-coral/10 hover:text-ponpon-coral px-2 py-1.5 rounded"
                  >
                    Return & Refund
                  </button>
                </div>
              ) : null}
            </div>
            <div className="my-1 border-t border-white/20"></div>
            <button
              type="button"
              onClick={handleLogoutClick}
              className="w-full px-4 py-2.5 text-left text-sm font-medium text-ponpon-coral transition hover:bg-ponpon-coral/10"
            >
              Logout
            </button>
          </nav>
        </div>
      ) : null}
    </div>
  );
};

export default ProfileDropdown;
